import axios from 'axios';
import { Query } from '../api';
import {
  SupportCase,
  CaseThread,
  CaseMessage,
  CaseNote,
  CreateSupportCaseRequest,
  InternalCreateSupportCaseRequest,
  UpdateSupportCaseRequest,
  CreateThreadRequest,
  CreateMessageRequest,
  InternalCreateMessageRequest,
  CreateNoteRequest,
  UpdateNoteRequest,
  SupportCaseFilters,
} from '../../types/support.types';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const SUPPORT_BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

// --- API class ---

export class SupportApi {
  // Internal endpoints
  static internalListCases(filters?: SupportCaseFilters) {
    const query = new Query('/internal/support/cases');
    if (filters) {
      if (filters.status) {
        query.filter({
          status: Array.isArray(filters.status)
            ? filters.status.join(',')
            : filters.status,
        });
      }
      if (filters.priority) {
        query.filter({
          priority: Array.isArray(filters.priority)
            ? filters.priority.join(',')
            : filters.priority,
        });
      }
      if (filters.account_id) {
        query.filter({ account_id: filters.account_id });
      }
      if (filters.assignee_user_id) {
        query.filter({ assignee_user_id: filters.assignee_user_id });
      }
      if (filters.search) {
        query.filter({ search: filters.search });
      }
      if (filters.page) query.page(filters.page);
      if (filters.size) query.size(filters.size);
      if (filters.sort) query.sort_by(filters.sort);
    }
    return query;
  }

  static internalGetCase(caseId: string) {
    return new Query(`/internal/support/cases/${caseId}`);
  }

  static internalCreateCase(data: InternalCreateSupportCaseRequest) {
    return new Query('/internal/support/cases', null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'support-case',
        attributes: data,
      },
      null,
    );
  }

  static internalUpdateCase(caseId: string, data: UpdateSupportCaseRequest) {
    return new Query(`/internal/support/cases/${caseId}`, null, {
      httpMethod: 'patch',
    }).setData(
      {
        type: 'support-case',
        id: caseId,
        attributes: data,
      },
      null,
    );
  }

  // Threads
  static internalListThreads(caseId: string) {
    return new Query(`/internal/support/cases/${caseId}/threads`);
  }

  static internalCreateThread(caseId: string, data: CreateThreadRequest) {
    return new Query(`/internal/support/cases/${caseId}/threads`, null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'case-thread',
        attributes: data,
      },
      null,
    );
  }

  // Messages
  static internalListMessages(caseId: string, threadId: string) {
    return new Query(
      `/internal/support/cases/${caseId}/threads/${threadId}/messages`,
    ).size(500);
  }

  static internalCreateMessage(
    caseId: string,
    threadId: string,
    data: InternalCreateMessageRequest,
  ) {
    return new Query(
      `/internal/support/cases/${caseId}/threads/${threadId}/messages`,
      null,
      { httpMethod: 'post' },
    ).setData(
      {
        type: 'case-message',
        attributes: data,
      },
      null,
    );
  }

  // Notes
  static internalListNotes(caseId: string) {
    return new Query(`/internal/support/cases/${caseId}/notes`).size(500);
  }

  static internalCreateNote(caseId: string, data: CreateNoteRequest) {
    return new Query(`/internal/support/cases/${caseId}/notes`, null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'case-note',
        attributes: data,
      },
      null,
    );
  }

  static internalUpdateNote(
    caseId: string,
    noteId: string,
    data: UpdateNoteRequest,
  ) {
    return new Query(
      `/internal/support/cases/${caseId}/notes/${noteId}`,
      null,
      {
        httpMethod: 'patch',
      },
    ).setData(
      {
        type: 'case-note',
        id: noteId,
        attributes: data,
      },
      null,
    );
  }

  static internalDeleteNote(caseId: string, noteId: string) {
    return new Query(
      `/internal/support/cases/${caseId}/notes/${noteId}`,
      null,
      {
        httpMethod: 'delete',
      },
    );
  }

  // Merge
  static internalMergeCase(sourceCaseId: string, targetCaseId: string) {
    return new Query(`/internal/support/cases/${sourceCaseId}/merge`, null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'case-merge',
        attributes: { target_case_id: targetCaseId },
      },
      null,
    );
  }

  // Internal note attachments
  static async internalUploadNoteAttachment(
    caseId: string,
    noteId: string,
    metadata: { file_name: string; content_type?: string; size_bytes?: number },
  ) {
    const response = await axios.post(
      `${SUPPORT_BASE_URL}/internal/support/cases/${caseId}/notes/${noteId}/attachments`,
      metadata,
      { withCredentials: true },
    );
    return response.data;
  }

  static async internalGetNoteAttachmentUrl(
    caseId: string,
    noteId: string,
    attachmentId: string,
  ) {
    const response = await axios.get(
      `${SUPPORT_BASE_URL}/internal/support/cases/${caseId}/notes/${noteId}/attachments/${attachmentId}`,
      { withCredentials: true },
    );
    return response.data;
  }

  // Internal message attachments
  static async internalUploadAttachment(
    caseId: string,
    messageId: string,
    metadata: { file_name: string; content_type?: string; size_bytes?: number },
  ) {
    const response = await axios.post(
      `${SUPPORT_BASE_URL}/internal/support/cases/${caseId}/messages/${messageId}/attachments`,
      metadata,
      { withCredentials: true },
    );
    return response.data;
  }

  static async internalGetAttachmentUrl(
    caseId: string,
    messageId: string,
    attachmentId: string,
  ) {
    const response = await axios.get(
      `${SUPPORT_BASE_URL}/internal/support/cases/${caseId}/messages/${messageId}/attachments/${attachmentId}`,
      { withCredentials: true },
    );
    return response.data;
  }

  // Customer endpoints
  static listCases(accountId: string, filters?: SupportCaseFilters) {
    const query = new Query(`/accounts/${accountId}/support/cases`);
    if (filters) {
      if (filters.status) {
        query.filter({
          status: Array.isArray(filters.status)
            ? filters.status.join(',')
            : filters.status,
        });
      }
      if (filters.priority) {
        query.filter({
          priority: Array.isArray(filters.priority)
            ? filters.priority.join(',')
            : filters.priority,
        });
      }
      if (filters.search) {
        query.filter({ search: filters.search });
      }
      if (filters.page) query.page(filters.page);
      if (filters.size) query.size(filters.size);
      if (filters.sort) query.sort_by(filters.sort);
    }
    return query;
  }

  static getCase(accountId: string, caseId: string) {
    return new Query(`/accounts/${accountId}/support/cases/${caseId}`);
  }

  static createCase(accountId: string, data: CreateSupportCaseRequest) {
    return new Query(`/accounts/${accountId}/support/cases`, null, {
      httpMethod: 'post',
    }).setData(
      {
        type: 'support-case',
        attributes: data,
      },
      null,
    );
  }

  // Customer threads
  static listThreads(accountId: string, caseId: string) {
    return new Query(`/accounts/${accountId}/support/cases/${caseId}/threads`);
  }

  // Customer messages
  static listMessages(accountId: string, caseId: string, threadId: string) {
    return new Query(
      `/accounts/${accountId}/support/cases/${caseId}/threads/${threadId}/messages`,
    ).size(500);
  }

  static createMessage(
    accountId: string,
    caseId: string,
    threadId: string,
    data: CreateMessageRequest,
  ) {
    return new Query(
      `/accounts/${accountId}/support/cases/${caseId}/threads/${threadId}/messages`,
      null,
      { httpMethod: 'post' },
    ).setData(
      {
        type: 'case-message',
        attributes: data,
      },
      null,
    );
  }
  // Customer attachments
  static async uploadAttachment(
    accountId: string,
    caseId: string,
    messageId: string,
    metadata: { file_name: string; content_type?: string; size_bytes?: number },
  ) {
    const response = await axios.post(
      `${SUPPORT_BASE_URL}/accounts/${accountId}/support/cases/${caseId}/messages/${messageId}/attachments`,
      metadata,
      { withCredentials: true },
    );
    return response.data;
  }

  static async getAttachmentUrl(
    accountId: string,
    caseId: string,
    messageId: string,
    attachmentId: string,
  ) {
    const response = await axios.get(
      `${SUPPORT_BASE_URL}/accounts/${accountId}/support/cases/${caseId}/messages/${messageId}/attachments/${attachmentId}`,
      { withCredentials: true },
    );
    return response.data;
  }
}

// --- Extract helpers ---

export const extractCaseData = (response: any): SupportCase => {
  const data = response.data || response;
  if (data.attributes) {
    return { id: data.id, ...data.attributes };
  }
  return data;
};

export const extractCaseListData = (response: any): SupportCase[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractCaseData);
  }
  return [];
};

export const extractThreadData = (response: any): CaseThread => {
  const data = response.data || response;
  if (data.attributes) {
    return { id: data.id, ...data.attributes };
  }
  return data;
};

export const extractThreadListData = (response: any): CaseThread[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractThreadData);
  }
  return [];
};

export const extractMessageData = (response: any): CaseMessage => {
  const data = response.data || response;
  if (data.attributes) {
    return { id: data.id, ...data.attributes };
  }
  return data;
};

export const extractMessageListData = (response: any): CaseMessage[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractMessageData);
  }
  return [];
};

export const extractNoteData = (response: any): CaseNote => {
  const data = response.data || response;
  if (data.attributes) {
    return { id: data.id, ...data.attributes };
  }
  return data;
};

export const extractNoteListData = (response: any): CaseNote[] => {
  const data = response.data || response;
  if (Array.isArray(data)) {
    return data.map(extractNoteData);
  }
  return [];
};

export const extractMeta = (
  response: any,
): { count: number; [key: string]: any } | undefined => {
  return response?.meta;
};
